/**
 * Mega Menu Component
 * Handles 4-level navigation with collapsible submenu functionality
 *
 * NOTE: MenuDrawer, HeaderDrawer, trapFocus, removeTrapFocus, and
 * getFocusableElements are defined in global.js — do NOT redeclare here.
 */

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.collapsibles = this.querySelectorAll('[data-mega-menu-collapsible]');

    this.init();
  }

  init() {
    if (!this.details || !this.summary) return;

    // Handle main menu open/close
    this.setupMainMenu();

    // Handle collapsible submenus
    this.setupCollapsibleSubmenus();

    // Handle click outside to close
    this.setupClickOutside();

    // Handle keyboard navigation
    this.setupKeyboardNav();
  }

  setupMainMenu() {
    // Close menu when clicking outside
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();

      if (this.details.hasAttribute('open')) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    });

    // Close other menus when opening this one
    this.details.addEventListener('toggle', () => {
      if (this.details.hasAttribute('open')) {
        this.closeOtherMenus();
      }
    });
  }

  setupCollapsibleSubmenus() {
    this.collapsibles.forEach((collapsible) => {
      const summary = collapsible.querySelector('summary');

      if (!summary) return;

      // Prevent default details behavior for custom handling
      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (collapsible.hasAttribute('open')) {
          this.closeCollapsible(collapsible);
        } else {
          this.openCollapsible(collapsible);
        }
      });

      // Handle keyboard interaction
      summary.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          summary.click();
        }
      });
    });
  }

  setupClickOutside() {
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target) && this.details.hasAttribute('open')) {
        this.closeMenu();
      }
    });
  }

  setupKeyboardNav() {
    // Close menu on Escape key
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.details.hasAttribute('open')) {
        this.closeMenu();
        this.summary.focus();
      }
    });
  }

  openMenu() {
    this.details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    this.details.removeAttribute('open');
    this.summary.setAttribute('aria-expanded', 'false');

    // Close all collapsible submenus when closing main menu
    this.collapsibles.forEach((collapsible) => {
      this.closeCollapsible(collapsible);
    });
  }

  closeOtherMenus() {
    const allMegaMenus = document.querySelectorAll('header-menu');
    allMegaMenus.forEach((menu) => {
      if (menu !== this && menu.details && menu.details.hasAttribute('open')) {
        menu.closeMenu();
      }
    });
  }

  openCollapsible(collapsible) {
    collapsible.setAttribute('open', '');
    const summary = collapsible.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }
  }

  closeCollapsible(collapsible) {
    collapsible.removeAttribute('open');
    const summary = collapsible.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }
  }
}

// Define the custom element
if (!customElements.get('header-menu')) {
  customElements.define('header-menu', MegaMenu);
}

// Close mega menus when scrolling
document.addEventListener('DOMContentLoaded', () => {
  let lastScrollTop = 0;
  window.addEventListener('scroll', () => {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    if (Math.abs(st - lastScrollTop) > 50) {
      document.querySelectorAll('header-menu').forEach((menu) => {
        if (menu.details && menu.details.hasAttribute('open')) {
          menu.closeMenu();
        }
      });
    }
    lastScrollTop = st <= 0 ? 0 : st;
  }, false);
});
